import { Checkbox, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select } from "@material-ui/core";
import { ChevronLeft, ChevronRight, Close, ExpandLess, ExpandMore, ImageOutlined, Search } from "@material-ui/icons";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { workspaceIntegrations } from "apis-v2/callables";
import { Button } from "js/Components/Button";
import WorkspaceController from "js/controllers/WorkspaceController";
import { FileType, fileTypesToMimeTypes } from "js/core/utilities/fileSourcesAndTypes";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import Spinner from "js/react/components/Spinner";
import { BrandfolderDialog } from "js/react/views/UserOptions/panes/IntegrationsPane/Components/BrandfolderDialog";
import { themeColors } from "js/react/sharedStyles";

const RESULTS_HEIGHT = "min(360px, calc(100vh - 350px))";
const SHARED_COLLECTIONS_SCOPE = "__shared_collections__";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    padding-bottom: 30px;
    font-size: 14px;
`;

const Message = styled.div<{ $error?: boolean }>`
    color: ${props => props.$error ? themeColors.warning : "#666"};
    line-height: 1.5;
`;

const FilterBar = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;

    @media (max-width: 700px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

const FilterField = styled.label<{ $grow?: boolean }>`
    display: flex;
    flex: ${props => props.$grow ? "1 1 260px" : "0 1 240px"};
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    color: #555;
    font-size: 11px;
    font-weight: 600;

    > span {
        text-transform: uppercase;
    }
`;

const CompactSelect = styled(Select)`
    && {
        height: 40px;

        .MuiSelect-select {
            display: flex;
            align-items: center;
            box-sizing: border-box;
            height: 40px;
            padding-top: 0;
            padding-bottom: 0;
            text-transform: none !important;
        }
    }
`;

const SearchField = styled.div`
    position: relative;

    > .MuiSvgIcon-root {
        position: absolute;
        top: 50%;
        left: 14px;
        color: #11a9e2;
        pointer-events: none;
        transform: translateY(-50%);
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    top: 50%;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: transparent;
    color: #bbb;
    cursor: pointer;
    transform: translateY(-50%);

    .MuiSvgIcon-root {
        font-size: 18px;
    }
`;

const SearchInput = styled.input`
    box-sizing: border-box;
    width: 100%;
    height: 40px;
    border: 1px solid #bbb;
    outline: none;
    padding: 0 40px 0 44px;
    font: inherit;
    font-size: 13px;

    &::placeholder {
        color: #999;
    }
`;

const ResultsArea = styled.div`
    position: relative;
    height: ${RESULTS_HEIGHT};
    min-height: 260px;
`;

const AssetList = styled(List)`
    &&& {
        height: 100%;
        overflow-y: auto;
        border: 1px solid ${themeColors.lightGray};
        padding: 0;
    }

    .MuiListItem-root {
        min-height: 60px;
        padding: 6px 16px;
    }

    .MuiListItem-root + .MuiListItem-root {
        border-top: 1px solid ${themeColors.lightGray};
    }

    .MuiListItemIcon-root {
        min-width: 60px;
    }

    .MuiListItemText-root {
        min-width: 0;
        margin: 0;
    }

    .MuiListItemText-primary {
        display: -webkit-box;
        overflow: hidden;
        overflow-wrap: anywhere;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-height: 20px;
    }
`;

const LoadingOverlay = styled.div`
    position: absolute;
    z-index: 1;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 100%;
    padding: 24px;
    border: 1px solid ${themeColors.lightGray};
    color: #666;
    text-align: center;

    .MuiSvgIcon-root {
        margin-bottom: 8px;
        color: #aaa;
        font-size: 32px;
    }

    strong {
        color: #444;
        font-size: 15px;
    }

    span {
        margin-top: 4px;
    }
`;

const TextButton = styled.button`
    display: flex;
    align-items: center;
    gap: 2px;
    margin-top: 12px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${themeColors.ui_blue};
    cursor: pointer;
    font: inherit;

    .MuiSvgIcon-root {
        font-size: 20px;
    }
`;

const Thumbnail = styled.img`
    width: 48px;
    height: 48px;
    object-fit: contain;
`;

const Pagination = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    min-height: 30px;
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ExpandButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #666;
    cursor: pointer;
`;

const RowControl = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
`;

const CommitButton = styled(Button)`
    &:disabled:hover:before {
        opacity: 0;
    }
`;

const AttachmentItem = styled(ListItem)`
    && {
        min-height: 48px;
        padding-left: 76px;
    }
`;

const PaginationButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid ${themeColors.ui_blue};
    background: white;
    color: ${themeColors.ui_blue};
    cursor: pointer;

    &:disabled {
        border-color: ${themeColors.lightGray};
        color: #aaa;
        cursor: default;
    }
`;

type Item = { id: string; name: string };
type Collection = Item & { brandfolderId?: string };
type Attachment = { id: string; name: string; mimeType: string };
type Asset = { id: string; name: string; thumbnailUrl?: string; selectionToken: string; attachments: Attachment[] };
type SelectedAttachment = {
    asset: Asset;
    attachment: Attachment;
    brandfolderId?: string;
    collectionId?: string;
};

type Props = {
    fileTypes: FileType[];
    handleImportedFile: (file: File) => void;
    handleImportedFiles?: (files: File[]) => void;
    multiple?: boolean;
};

export default function BrandfolderPicker({ fileTypes, handleImportedFile, handleImportedFiles, multiple }: Props) {
    const workspaceId = WorkspaceController.workspaceId;
    const [connected, setConnected] = useState<boolean | null>(null);
    const [brandfolders, setBrandfolders] = useState<Item[]>([]);
    const [brandfolderId, setBrandfolderId] = useState("");
    const [collections, setCollections] = useState<Collection[]>([]);
    const [collectionId, setCollectionId] = useState("");
    const [assets, setAssets] = useState<Asset[]>([]);
    const [selectedAttachments, setSelectedAttachments] = useState<SelectedAttachment[]>([]);
    const [expandedAssetIds, setExpandedAssetIds] = useState<string[]>([]);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const supportedMimeTypes = fileTypesToMimeTypes(fileTypes);
    const accessibleBrandfolderIds = new Set(brandfolders.map(item => item.id));
    const sharedCollections = collections.filter(item => !item.brandfolderId || !accessibleBrandfolderIds.has(item.brandfolderId));
    const sharedCollectionsSelected = brandfolderId === SHARED_COLLECTIONS_SCOPE;
    const activeBrandfolderId = sharedCollectionsSelected ? undefined : brandfolderId || undefined;
    const activeCollections = sharedCollectionsSelected ? sharedCollections : collections.filter(item => item.brandfolderId === brandfolderId);

    const clearSearch = () => {
        setSearch("");
        setPage(1);
    };

    const loadConnection = useCallback(() => {
        setError("");
        workspaceIntegrations.getBrandfolderConnection({ workspaceId })
            .then(status => setConnected(status.connected))
            .catch(() => {
                setConnected(false);
                setError("Could not load your Brandfolder connection.");
            });
    }, [workspaceId]);

    useEffect(loadConnection, [loadConnection]);

    useEffect(() => {
        if (!connected) return;
        let active = true;
        setBusy(true);
        setError("");
        Promise.all([
            workspaceIntegrations.discoverBrandfolderBrandfolders({ workspaceId }),
            workspaceIntegrations.discoverBrandfolderCollections({ workspaceId })
        ])
            .then(([brandfolderItems, collectionItems]) => {
                if (!active) return;
                setBrandfolders(brandfolderItems);
                setCollections(collectionItems);
                const brandfolderIds = new Set(brandfolderItems.map(item => item.id));
                const firstSharedCollection = collectionItems.find(item => !item.brandfolderId || !brandfolderIds.has(item.brandfolderId));
                setBrandfolderId(brandfolderItems[0]?.id || (firstSharedCollection ? SHARED_COLLECTIONS_SCOPE : ""));
                setCollectionId(brandfolderItems.length > 0 ? "" : firstSharedCollection?.id || "");
            })
            .catch(err => active && setError(err.message || "Could not load Brandfolder content."))
            .finally(() => active && setBusy(false));
        return () => {
            active = false;
        };
    }, [workspaceId, connected]);

    useEffect(() => {
        const timeout = setTimeout(() => setAppliedSearch(search), 350);
        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        if (!connected || (!activeBrandfolderId && !collectionId)) return;
        let active = true;
        setBusy(true);
        setError("");
        workspaceIntegrations.listBrandfolderAssets({ workspaceId, brandfolderId: activeBrandfolderId, collectionId: collectionId || undefined, search: appliedSearch, page })
            .then(result => {
                if (!active) return;
                setAssets(result.assets);
                setHasMore(result.hasMore);
                setExpandedAssetIds([]);
            })
            .catch(err => active && setError(err.message || "Could not load Brandfolder assets."))
            .finally(() => active && setBusy(false));
        return () => {
            active = false;
        };
    }, [workspaceId, connected, activeBrandfolderId, collectionId, appliedSearch, page]);

    const attachmentKey = (asset: Asset, attachment: Attachment) => `${asset.id}:${attachment.id}`;
    const isAttachmentSelected = (asset: Asset, attachment: Attachment) => selectedAttachments.some(item => attachmentKey(item.asset, item.attachment) === attachmentKey(asset, attachment));
    const toggleAssetExpanded = (assetId: string) => setExpandedAssetIds(current => current.includes(assetId) ? current.filter(id => id !== assetId) : [...current, assetId]);

    const toggleAttachment = (asset: Asset, attachment: Attachment) => {
        const key = attachmentKey(asset, attachment);
        setSelectedAttachments(current => {
            const selected = current.some(item => attachmentKey(item.asset, item.attachment) === key);
            if (selected) return current.filter(item => attachmentKey(item.asset, item.attachment) !== key);
            const item = { asset, attachment, brandfolderId: activeBrandfolderId, collectionId: collectionId || undefined };
            return multiple ? [...current, item] : [item];
        });
    };

    const importSelectedAttachments = async () => {
        setBusy(true);
        setError("");
        try {
            const files: File[] = [];
            for (const selection of selectedAttachments) {
                const blob = await workspaceIntegrations.downloadBrandfolderAttachment({
                    workspaceId,
                    brandfolderId: selection.brandfolderId,
                    collectionId: selection.collectionId,
                    assetId: selection.asset.id,
                    attachmentId: selection.attachment.id,
                    selectionToken: selection.asset.selectionToken
                });
                files.push(new File([blob], selection.attachment.name, { type: selection.attachment.mimeType }));
            }
            if (multiple) {
                handleImportedFiles?.(files);
            } else if (files[0]) {
                handleImportedFile(files[0]);
            }
            setSelectedAttachments([]);
        } catch (err) {
            setError(err.message || "Could not download the selected files.");
        } finally {
            setBusy(false);
        }
    };

    const renderEmptyState = (icon: React.ReactNode, title: string, message: string, action?: React.ReactNode) => (
        <EmptyState>
            {icon}
            <strong>{title}</strong>
            <span>{message}</span>
            {action}
        </EmptyState>
    );

    const renderResults = () => {
        if (assets.length > 0) {
            return (<AssetList aria-label="Brandfolder assets">
                {assets.map(asset => {
                    const attachments = asset.attachments.filter(attachment => supportedMimeTypes.includes(attachment.mimeType));
                    const expanded = expandedAssetIds.includes(asset.id);
                    const isSingleAttachment = attachments.length === 1;
                    const singleAttachmentSelected = isSingleAttachment && isAttachmentSelected(asset, attachments[0]);
                    return (<React.Fragment key={asset.id}>
                        <ListItem button disabled={busy} aria-label={asset.name} title={asset.name} onClick={() => {
                            if (isSingleAttachment) {
                                toggleAttachment(asset, attachments[0]);
                            } else if (attachments.length > 1) {
                                toggleAssetExpanded(asset.id);
                            }
                        }}>
                            <ListItemIcon>{asset.thumbnailUrl ? <Thumbnail src={asset.thumbnailUrl} alt="" /> : <ImageOutlined />}</ListItemIcon>
                            <ListItemText primary={asset.name} secondary={`${attachments.length} attachment${attachments.length === 1 ? "" : "s"}`} title={asset.name} />
                            {isSingleAttachment && <RowControl><Checkbox
                                checked={singleAttachmentSelected}
                                inputProps={{ "aria-label": `${singleAttachmentSelected ? "Remove" : "Select"} ${attachments[0].name}` }}
                                onClick={event => event.stopPropagation()}
                                onChange={() => toggleAttachment(asset, attachments[0])}
                            /></RowControl>}
                            {attachments.length > 1 && <RowControl><ExpandButton type="button" aria-label={`${expanded ? "Collapse" : "Expand"} ${asset.name}`} onClick={event => {
                                event.stopPropagation();
                                toggleAssetExpanded(asset.id);
                            }}>
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                            </ExpandButton></RowControl>}
                        </ListItem>
                        {expanded && attachments.map(attachment => {
                            const selected = isAttachmentSelected(asset, attachment);
                            return (<AttachmentItem key={attachment.id} button disabled={busy} onClick={() => toggleAttachment(asset, attachment)}>
                                <ListItemText primary={attachment.name} title={attachment.name} />
                                <RowControl><Checkbox
                                    checked={selected}
                                    inputProps={{ "aria-label": `${selected ? "Remove" : "Select"} ${attachment.name}` }}
                                    onClick={event => event.stopPropagation()}
                                    onChange={() => toggleAttachment(asset, attachment)}
                                /></RowControl>
                            </AttachmentItem>);
                        })}
                    </React.Fragment>);
                })}
            </AssetList>);
        }

        return renderEmptyState(<ImageOutlined />, search ? "No assets match your search" : "No assets in this collection", search ? "Try a different search term or clear the search." : "Choose another collection or try searching for an asset.", search && <TextButton onClick={clearSearch}>Clear search</TextButton>);
    };

    const initialLoading = connected === null || (connected && brandfolders.length === 0 && collections.length === 0 && busy);
    const noContent = connected && brandfolders.length === 0 && collections.length === 0 && !busy;
    const scopeCount = brandfolders.length + (sharedCollections.length > 0 ? 1 : 0);

    return (<Container>
        {initialLoading ? <ResultsArea role="status" aria-label="Loading Brandfolder"><LoadingOverlay><Spinner /></LoadingOverlay></ResultsArea> : !connected ? <>
            <Message>Connect your Brandfolder account to browse assets available to your API key.</Message>
            <Button blue onClick={() => ShowDialog(BrandfolderDialog, { onConnected: loadConnection })}>Connect Brandfolder</Button>
        </> : noContent ? <Message>No Brandfolders or Collections are available to this API key.</Message> : <>
            <FilterBar>
                {scopeCount > 1 && <FilterField>
                    <span>Brandfolder</span>
                    <CompactSelect variant="outlined" fullWidth inputProps={{ "aria-label": "Brandfolder" }} value={brandfolderId} disabled={busy} onChange={event => {
                        const nextBrandfolderId = event.target.value as string;
                        setBrandfolderId(nextBrandfolderId);
                        setCollectionId(nextBrandfolderId === SHARED_COLLECTIONS_SCOPE ? sharedCollections[0]?.id || "" : "");
                        setPage(1);
                    }}>
                        {brandfolders.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                        {sharedCollections.length > 0 && <MenuItem value={SHARED_COLLECTIONS_SCOPE}>Shared Collections</MenuItem>}
                    </CompactSelect>
                </FilterField>}
                {activeCollections.length > 0 && <FilterField>
                    <span>{sharedCollectionsSelected ? "Accessible Collection" : "Collection"}</span>
                    <CompactSelect variant="outlined" fullWidth displayEmpty inputProps={{ "aria-label": "Brandfolder Collection" }} value={collectionId} disabled={busy} renderValue={value => value ? activeCollections.find(item => item.id === value)?.name || "Select a Collection" : "All assets"} onChange={event => {
                        setCollectionId(event.target.value as string);
                        setPage(1);
                    }}>
                        {!sharedCollectionsSelected && <MenuItem value="">All assets</MenuItem>}
                        {activeCollections.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                    </CompactSelect>
                </FilterField>}
                <FilterField $grow>
                    <span>Search assets</span>
                    <SearchField>
                        <Search />
                        <SearchInput aria-label="Search Brandfolder assets" type="text" value={search} onChange={event => {
                            setSearch(event.target.value);
                            setPage(1);
                        }} placeholder="Search assets" disabled={busy} />
                        {search && <ClearSearchButton type="button" aria-label="Clear search" onClick={clearSearch}><Close /></ClearSearchButton>}
                    </SearchField>
                </FilterField>
            </FilterBar>
            <ResultsArea aria-busy={busy}>
                {renderResults()}
                {busy && <LoadingOverlay role="status" aria-label="Loading assets"><Spinner /></LoadingOverlay>}
            </ResultsArea>
            <Pagination>
                <PaginationControls>
                    <Message>Page {page}</Message>
                    <PaginationButton type="button" aria-label="Previous page" title="Previous page" disabled={busy || page === 1} onClick={() => setPage(page - 1)}><ChevronLeft /></PaginationButton>
                    <PaginationButton type="button" aria-label="Next page" title="Next page" disabled={busy || !hasMore} onClick={() => setPage(page + 1)}><ChevronRight /></PaginationButton>
                </PaginationControls>
                <CommitButton blue disabled={busy || selectedAttachments.length === 0} onClick={importSelectedAttachments}>
                    {multiple ? `Import selected (${selectedAttachments.length})` : "Import"}
                </CommitButton>
            </Pagination>
        </>}
        {error && <Message $error role="alert">{error}</Message>}
    </Container>);
}
